import type { PlanId } from '@shopgnd/shared';
import { sanitizeLoginNextPath } from '@/lib/loginNavigation';
import type { SignupGoals } from '@/lib/signupGoals';

const PLAN_IDS: PlanId[] = ['starter', 'growth', 'pro'];

/** Canonical join funnel path (unified store + Find a Pro onboarding). */
export const JOIN_PATH = '/join';

export function parseJoinPlanId(raw: string | null | undefined): PlanId | null {
  const id = raw?.trim().toLowerCase();
  if (!id) return null;
  return PLAN_IDS.includes(id as PlanId) ? (id as PlanId) : null;
}

/** Effective plan for vendor create: honor marketing choice, else Starter (upgrade in hub). */
export function resolveJoinPlanId(raw: string | null | undefined): PlanId {
  return parseJoinPlanId(raw) ?? 'starter';
}

export function buildJoinPath(options?: {
  intent?: string | null;
  plan?: string | null;
  next?: string | null;
  extra?: Record<string, string | null | undefined>;
}): string {
  const params = new URLSearchParams();
  const intent = options?.intent?.trim();
  if (intent) params.set('intent', intent);
  const plan = parseJoinPlanId(options?.plan ?? null);
  if (plan && plan !== 'starter') params.set('plan', plan);
  const safeNext = options?.next ? sanitizeLoginNextPath(options.next) : null;
  if (safeNext) params.set('next', safeNext);
  if (options?.extra) {
    for (const [k, v] of Object.entries(options.extra)) {
      if (v) params.set(k, v);
    }
  }
  const qs = params.toString();
  return qs ? `${JOIN_PATH}?${qs}` : JOIN_PATH;
}

export function joinPathFromGoals(
  goals: SignupGoals,
  planId?: string | null,
  next?: string | null,
  extra?: Record<string, string | null | undefined>,
): string {
  let intent: string | null = null;
  if (goals.sell && goals.pro) intent = 'business';
  else if (goals.pro || goals.serviceIntent === 'trades') intent = 'pro';
  else if (goals.serviceIntent) intent = goals.serviceIntent;
  else if (goals.sell) intent = 'sell';

  return buildJoinPath({ intent, plan: planId, next, extra });
}

export function isJoinPath(pathname: string): boolean {
  return pathname === JOIN_PATH || pathname.startsWith(`${JOIN_PATH}/`);
}

/** Legacy onboard URLs → /join (preserve query; inject intent when missing). */
export function legacyOnboardToJoin(
  pathname: string,
  search: string,
): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (pathname.startsWith('/vendor/onboard')) {
    if (!params.get('intent')) params.set('intent', 'sell');
    const qs = params.toString();
    return qs ? `${JOIN_PATH}?${qs}` : `${JOIN_PATH}?intent=sell`;
  }
  if (pathname.startsWith('/pro/onboard')) {
    if (!params.get('intent')) params.set('intent', 'pro');
    const qs = params.toString();
    return qs ? `${JOIN_PATH}?${qs}` : `${JOIN_PATH}?intent=pro`;
  }
  return null;
}
